import React from 'react';
import { Button } from '../Button/Button';
import { ExclamationCircleIcon, ComputerDesktopIcon } from './icons';
import type { MatchModalProps } from './MatchModal.types';
import { useMatchModalTranslation } from './i18n';

/**
 * MatchModal - Componente para mostrar coincidencias encontradas al validar datos en tiempo real.
 *
 * Este componente es útil para prevenir la creación de duplicados en el sistema,
 * permitiendo al usuario ver registros existentes con datos similares.
 *
 * ## Degradación del encabezado
 *
 * `fieldName` solo alimenta el título por defecto, así que su ausencia no impide
 * usar el modal: se muestra un encabezado genérico y las coincidencias y el
 * botón de acción siguen operativos. El contrato lo declaraba obligatorio y el
 * título llamaba a `.toLowerCase()` sin guarda, de modo que un campo mal
 * configurado —o un texto que llega vacío desde i18n— lanzaba un `TypeError` en
 * render y activaba el error boundary del consumidor: un dato decorativo tumbaba
 * la pantalla completa.
 *
 * Por la misma razón el componente no confía en que `matches` sea un arreglo:
 * TypeScript no protege en runtime y `.map()` sobre un valor ausente reproduce
 * exactamente el mismo fallo desproporcionado.
 *
 * @see i18n.ts - Claves de traducción y diccionarios ES/EN
 * @see MatchModal.types - `viewLabel` / `continueLabel` para sobrescribir textos
 *
 * @example
 * ```tsx
 * <MatchModal
 *   isOpen={showMatches}
 *   onClose={() => setShowMatches(false)}
 *   fieldName="Nombre"
 *   matches={results}
 *   onView={(match) => handleView(match)}
 *   onContinue={() => handleContinue()}
 * />
 * ```
 */
export const MatchModal: React.FC<MatchModalProps> = ({
  isOpen,
  // `onClose` no se desestructura a propósito: el modal no ofrece ningún control
  // de cierre (decisión de diseño, ver MatchModal.types). Sigue en el contrato
  // para no romper a los consumidores que ya la pasan.
  matches,
  fieldName,
  onView,
  onContinue,
  title,
  viewLabel,
  continueLabel,
  className = '',
}) => {
  // El hook va antes del retorno temprano: llamarlo después lo volvería
  // condicional y React fallaría al alternar `isOpen`.
  const { t } = useMatchModalTranslation();

  if (!isOpen) return null;

  // ===== DATOS BLINDADOS =====
  // `typeof` y no truthiness: el consumidor puede pasar en runtime cualquier cosa
  // que el tipo `string` no garantiza (un `header` sin definir, un número, null).
  const nombreCampo = typeof fieldName === 'string' ? fieldName.trim() : '';
  const coincidencias = Array.isArray(matches) ? matches : [];

  // ===== TEXTOS =====
  // `||` y no `??`: una cadena vacía —clave de i18n sin resolver en el host— se
  // trata como ausente, para no dejar un encabezado o un botón en blanco.
  const tituloModal =
    title ||
    (nombreCampo
      ? t('match_modal.title', { fieldName: nombreCampo.toLowerCase() })
      : t('match_modal.titleFallback'));
  const textoVer = viewLabel || t('match_modal.view');
  const textoContinuar = continueLabel || t('match_modal.continue');

  return (
    <div className={`fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200 ${className}`}>
      <div className="bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-xl shadow-2xl flex flex-col w-full max-w-2xl overflow-hidden relative">
        
        {/* Header - Icono + Título */}
        <div className="p-[20px] flex gap-5 items-center">
          <div className="bg-[#fcedc9] dark:bg-amber-900/20 flex items-center justify-center rounded-full shrink-0 size-[40px]">
            <ExclamationCircleIcon className="size-[24px] text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex flex-col justify-center pr-8">
            <h3 className="text-[20px] font-bold leading-[28px] tracking-tight text-content-primary dark:text-dark-content-primary">
              {tituloModal}
            </h3>
          </div>
        </div>

        {/* Body - Lista de coincidencias */}
        <div className="px-[20px] pb-[20px] flex flex-col gap-[20px] overflow-y-auto max-h-[385px] scrollbar-thin scrollbar-thumb-border-primary dark:scrollbar-thumb-dark-border-primary">
          <div className="flex flex-col items-start w-full">
            {coincidencias.map((match) => (
              <div 
                key={match.id}
                className="border border-border-primary dark:border-dark-border-primary h-[64px] px-[8px] flex items-center justify-between rounded-[8px] w-full mb-3 last:mb-0 transition-colors hover:bg-background-secondary dark:hover:bg-dark-bg-inverse/5"
              >
                <div className="flex items-center gap-[8px]">
                  {/* Icono de Computador/Desktop */}
                  <div className="bg-[#dbeefe] dark:bg-primary-900/20 flex items-center justify-center rounded-[16px] shrink-0 size-[32px]">
                    <ComputerDesktopIcon className="size-[18px] text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[16px] font-medium leading-[24px] text-content-primary dark:text-dark-content-primary truncate max-w-[300px]">
                      {match.name}
                    </span>
                  </div>
                </div>
                
                {/* Botón Ver */}
                <Button 
                  type="outline" 
                  size="xs" 
                  onClick={() => onView(match)}
                  className="w-[85.5px] h-[32px] !text-[12px] !font-medium"
                >
                  {textoVer}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-[24px] py-[16px] pb-[24px] flex items-center justify-end gap-[12px] shrink-0 w-full border-t border-border-secondary dark:border-dark-border-primary bg-background-secondary/30 dark:bg-dark-bg-inverse/5">
          <Button type="default" size="l" onClick={onContinue}>
            {textoContinuar}
          </Button>
        </div>
      </div>
    </div>
  );
};
